import log from '@/lib/logger';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Member, MemberUpdateCommentsRequest, MemberUpdateBasicInfoRequest} from "@/models/member.ts";
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {updateMemberComments, updateBasicMemberInformation} from "@/services/member-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {useEffect, useMemo, useState} from "react";
import {Textarea} from "@/components/tailwind/textarea.tsx";
import {MobilityOption} from "@/models/mobility-option.ts";
import {fetchAllMobilityOptions} from "@/services/mobility-option-services.ts";
import {MobilityOptionCombobox} from "@/components/mobility-option-combobox.tsx";

interface MemberEditCommentsDialogProps {
    isOpen: boolean;
    onSave: (data: Member) => void;
    onClose: () => void;
    memberData: Member;
}

const memberEditCommentsSchema = z.object({
    fastestMobilityOption: z.object({
        id: z.string(),
        name: z.string()
    }).nullable().optional(),
    generalComments: z.string()
        .trim()
        .transform(val => val === "" ? undefined : val)
        .optional(),
    availabilityComments: z.string()
        .trim()
        .transform(val => val === "" ? undefined : val)
        .optional(),
    currentNotes: z.string()
        .trim()
        .transform(val => val === "" ? undefined : val)
        .optional(),
});

export type MemberEditCommentsFormData = z.infer<typeof memberEditCommentsSchema>;

export default function MemberEditCommentsDialog({
                                                     isOpen,
                                                     onSave,
                                                     onClose,
                                                     memberData,
                                                 }: MemberEditCommentsDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token: authToken} = useAuth();
    const [mobilityOptions, setMobilityOptions] = useState<MobilityOption[]>([]);

    useEffect(() => {
        async function loadMobilityOptions() {
            try {
                const options = await fetchAllMobilityOptions(authToken);
                if (options) {
                    setMobilityOptions(options);
                }
            } catch (error) {
                log.error("Failed to load mobility options:", error);
            }
        }

        if (isOpen) {
            loadMobilityOptions();
        }
    }, [isOpen, authToken]);

    const defaultValues = useMemo(() => ({
        fastestMobilityOption: memberData.fastestMobilityOption,
        generalComments: memberData.generalComments ?? '',
        availabilityComments: memberData.availabilityComments ?? '',
        currentNotes: memberData.currentNotes ?? '',
    }), [memberData.fastestMobilityOption, memberData.generalComments, memberData.availabilityComments, memberData.currentNotes]);

    const {
        register,
        handleSubmit,
        setError,
        control,
        formState: {errors},
        reset
    } = useForm<MemberEditCommentsFormData>({
        resolver: zodResolver(memberEditCommentsSchema),
        defaultValues
    });

    useEffect(() => {
        if (isOpen && memberData) {
            reset(defaultValues);
        }
    }, [isOpen, memberData, reset, defaultValues]);

    async function saveData(data: MemberEditCommentsFormData): Promise<boolean> {
        try {
            // First update mobility option (basic member info)
            const basicData: MemberUpdateBasicInfoRequest = {
                memberId: memberData.id,
                firstName: memberData.firstName ?? '',
                lastName: memberData.lastName ?? '',
                title: memberData.title,
                dateOfBirth: memberData.dateOfBirth ?? null,
                salutationId: memberData.salutation?.id,
                fastestMobilityOptionId: data.fastestMobilityOption?.id
            };

            await updateBasicMemberInformation(authToken, basicData);

            // Then update comments
            const commentData: MemberUpdateCommentsRequest = {
                memberId: memberData.id,
                generalComments: data.generalComments,
                availabilityComments: data.availabilityComments,
                currentNotes: data.currentNotes,
            };

            const commentResponse = await updateMemberComments(authToken, commentData);

            if (commentResponse) {
                onSave(commentResponse);
            }
            return true;
        } catch (error: any) {
            log.error("Error updating member: ", error);
            if (error.status) {
                setError("root", {
                    message: tCommon("serverError.response-code-with-details", {
                        statusCode: error.status,
                        errorDetails: error.error
                    })
                });
            } else {
                setError("root", {message: tCommon("serverError.unknown-with-details", {errorDetails: error.message})});
            }

            return false;
        }

    }

    async function onSubmit(data: MemberEditCommentsFormData) {
        if (await saveData(data)) {
            handleClosing();
        }
    }

    function handleClosing() {
        reset();
        onClose();
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {t('memberEditDialog.additionalInfo.title')}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        tCommon("serverError.unknown")}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Controller
                            name="fastestMobilityOption"
                            control={control}
                            render={({field: {onChange, value}}) => (
                                <MobilityOptionCombobox
                                    value={value || null}
                                    onChange={onChange}
                                    options={mobilityOptions}
                                    label={t('memberEditDialog.additionalInfo.fields.mobilityOption.label')}
                                    data-testid="mobility-option-combobox"
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Field>
                            <Label>{t('memberEditDialog.comments.fields.general.label')}</Label>
                            <Textarea
                                {...register("generalComments", {
                                    setValueAs: (value) => value === '' ? undefined : value
                                })}
                                aria-label={t('memberEditDialog.comments.fields.general.label')}
                                aria-invalid={!!errors.generalComments}
                                placeholder={t('memberEditDialog.comments.fields.general.placeholder')}
                            />

                        </Field>
                        {errors.generalComments && (
                            <ValidationAlert
                                message={t(errors.generalComments?.message ||
                                    "memberEditDialog.comments.errors.unknown-generalComments-issue")}
                            />
                        )}
                    </div>

                    {memberData.memberStatus === 'ACTIVE_MEMBER' && (
                        <div>
                            <Field>
                                <Label>{t('memberEditDialog.comments.fields.availability.label')}</Label>
                                <Textarea
                                    {...register("availabilityComments", {
                                        setValueAs: (value) => value === '' ? undefined : value
                                    })}
                                    aria-label={t('memberEditDialog.comments.fields.availability.label')}
                                    aria-invalid={!!errors.availabilityComments}
                                    placeholder={t('memberEditDialog.comments.fields.availability.placeholder')}
                                />

                            </Field>
                            {errors.availabilityComments && (
                                <ValidationAlert
                                    message={t(errors.availabilityComments?.message ||
                                        "memberEditDialog.comments.errors.unknown-availability-issue")}
                                />
                            )}
                        </div>
                    )}

                    {memberData.memberStatus === 'ACTIVE_MEMBER' && memberData.isDriver && (
                        <div>
                            <Field>
                                <Label>{t('memberEditDialog.comments.fields.currentNotes.label')}</Label>
                                <Textarea
                                    {...register("currentNotes", {
                                        setValueAs: (value) => value === '' ? undefined : value
                                    })}
                                    aria-label={t('memberEditDialog.comments.fields.currentNotes.label')}
                                    aria-invalid={!!errors.currentNotes}
                                />
                            </Field>
                        </div>
                    )}

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                        >
                            {tCommon('buttons.cancel.text')}
                        </Button>
                        <Button
                            type="submit"
                            aria-label={tCommon('buttons.submit.ariaLabel')}
                        >
                            {tCommon('buttons.submit.text')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
